import { Constants } from '../../../../utils/constants';
import { formatNaira } from '../../../../utils/money';
import { formatLagos } from '../../../../utils/datetime';
import { DataTable, DateRangeFilter, FilterSelect, Pagination, SearchInput, StatusPill, TableCard } from '../../../ui';
import { useServerTable, wholeDayDates } from '../../../../hooks/useServerTable';
import SubMerchantDeactivated, { isSubMerchantDeactivated } from './SubMerchantDeactivated';

const typeOptions = Object.entries(Constants.ledgerEntryTypes).map(([value, { label }]) => ({ value, label }));

const amountTone = {
  credit: 'whitespace-nowrap font-medium text-success-700',
  debit: 'whitespace-nowrap font-medium text-danger-700',
};

const EntryType = ({ type }) => {
  const known = Constants.ledgerEntryTypes[type];
  return <StatusPill status={known?.label ?? type} tone={known?.tone} />;
};

const LedgerTable = ({ endpoint }) => {
  const table = useServerTable(endpoint, {
    filters: { type: '' },
    dateParams: wholeDayDates,
    fallbackError: 'We could not load the ledger. Please try again.',
  });

  if (isSubMerchantDeactivated(table.requestError)) return <SubMerchantDeactivated />;

  const columns = [
    {
      key: 'created_at',
      header: 'Date (WAT)',
      render: (row) => <span className="whitespace-nowrap">{formatLagos(row.created_at)}</span>,
      filter: (
        <DateRangeFilter
          label="Date"
          from={table.from}
          to={table.to}
          onFromChange={table.setFrom}
          onToChange={table.setTo}
        />
      ),
    },
    {
      key: 'type',
      header: 'Type',
      render: (row) => <EntryType type={row.type} />,
      filter: (
        <FilterSelect
          value={table.filters.type ?? ''}
          onChange={(value) => table.setFilter('type', value)}
          options={typeOptions}
          label="All"
          ariaLabel="Filter by type"
        />
      ),
    },
    {
      key: 'pan',
      header: 'PAN',
      render: (row) => row.pan || '—',
    },
    {
      key: 'cash_tap_transaction_id',
      header: 'Tap Transaction ID',
      render: (row) => row.cash_tap_transaction_id || '—',
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (row) => (
        <span className={Number(row.amount) < 0 ? amountTone.debit : amountTone.credit}>{formatNaira(row.amount)}</span>
      ),
    },
    {
      key: 'balance_after',
      header: 'Balance After',
      align: 'right',
      render: (row) => formatNaira(row.balance_after),
    },
  ];

  return (
    <TableCard
      title="Ledger"
      summary={{
        label: 'Balance',
        value: formatNaira(table.envelope?.balance),
        loading: table.loading && !table.envelope,
      }}
      toolbar={
        <SearchInput value={table.search} onChange={table.setSearch} placeholder="Search PAN or tap transaction ID" />
      }
      footer={
        table.total > 0 && (
          <Pagination
            page={table.page}
            pageCount={table.pageCount}
            pageSize={table.pageSize}
            onPageChange={table.setPage}
            onPageSizeChange={table.setPageSize}
          />
        )
      }
    >
      <DataTable
        columns={columns}
        rows={table.rows}
        rowKey={(row) => row.id}
        serial
        serialStart={table.page * table.pageSize}
        loading={table.loading}
        error={table.error}
        invalidDateRange={table.invalidDateRange}
        empty={{ title: 'No ledger entries', message: 'No entries match the selected filters.' }}
      />
    </TableCard>
  );
};

export default LedgerTable;
